import { dayKey, formatCompactDate, formatCount, formatDate, formatMonthDay, wallClock } from '../format';
import type { ChatStats, PersonStats } from '../types';

/**
 * UI-only formatting on top of ../format.ts. Times are wall-clock ms.
 */

/** Distinct, accessible-on-white hues for people (used for bars and dots, not text). */
export const PERSON_COLORS = ['#5f33e1', '#e0569b', '#14917f', '#e08a12', '#3a7bd5', '#9b59d0', '#c4512b', '#5d6b82'] as const;

export function personColor(index: number): string {
  const size = PERSON_COLORS.length;
  return PERSON_COLORS[((Math.trunc(index) % size) + size) % size]!;
}

/** 16.376 -> "16.4", 3 -> "3", 1234.5 -> "1,235" */
export function formatAverage(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0';
  if (value >= 100) return formatCount(value);
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** "2024년 3월 1일 ~ 6월 1일" (year repeated only when it changes). */
export function formatPeriod(firstAt: number, lastAt: number): string {
  if (dayKey(firstAt) === dayKey(lastAt)) return formatDate(firstAt);
  if (wallClock(firstAt).year === wallClock(lastAt).year) return `${formatDate(firstAt)} ~ ${formatMonthDay(lastAt)}`;
  return `${formatDate(firstAt)} ~ ${formatDate(lastAt)}`;
}

/** "2024.3.1 – 2024.6.1" */
export function formatCompactPeriod(firstAt: number, lastAt: number): string {
  if (dayKey(firstAt) === dayKey(lastAt)) return formatCompactDate(firstAt);
  return `${formatCompactDate(firstAt)} – ${formatCompactDate(lastAt)}`;
}

/** Cover title: the room name for groups, "{A} · {B}" for pairs (spec §6.2). */
export function roomTitle(stats: Pick<ChatStats, 'roomKind' | 'title' | 'people'>): string {
  const names = stats.people.map((person) => person.name);
  if (stats.roomKind === 'pair') return `${names[0] ?? ''} · ${names[1] ?? ''}`;
  const title = stats.title?.trim();
  if (title) return title;
  if (names.length <= 3) return names.join(', ');
  return `${names[0]}, ${names[1]} 외 ${names.length - 2}명`;
}

/** People sorted by message count (stable for ties). */
export function peopleByMessages(people: readonly PersonStats[]): PersonStats[] {
  return people
    .map((person, order) => ({ person, order }))
    .sort((a, b) => b.person.messages - a.person.messages || a.order - b.order)
    .map(({ person }) => person);
}

export interface VisibleSplit<T> {
  readonly shown: readonly T[];
  readonly hidden: number;
}

export function limitItems<T>(items: readonly T[], limit: number): VisibleSplit<T> {
  if (items.length <= limit) return { shown: items, hidden: 0 };
  return { shown: items.slice(0, limit), hidden: items.length - limit };
}

/** 2.345 -> "2.3", 2 -> "2" */
export function formatOneDecimal(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** Local calendar date for file names: 20261104 */
export function compactLocalDate(now: Date): string {
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
}

/** Code-point length, so emoji and Hangul count like people expect. */
export function codePointLength(value: string): number {
  return Array.from(value).length;
}

export function truncateCodePoints(value: string, max: number): string {
  const characters = Array.from(value);
  return characters.length <= max ? value : characters.slice(0, max).join('');
}
